import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AlertCircle, CheckCircle2, Info, MailCheck } from 'lucide-react';
import AuthShell from '../components/AuthShell';
import { Spinner } from '../components/ui';
import { authService, getErrorMessage } from '../services/api';
import { getPendingInviteCode } from '../utils/invite';

// The backend sends one link per address every 2 minutes (RATE_LIMIT_ACCOUNT_EMAIL_SECONDS); sooner, it answers the
// same but sends nothing, so the button waits as long.
const RESEND_COOLDOWN_SECONDS = 120;

type CheckEmailState = { email?: string; sent?: boolean; fromLogin?: boolean } | null;

function formatCountdown(seconds: number): string {
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

/**
 * ACC-16: a new account waits for its email confirmation. Shown after registering, after logging in to an account that
 * isn't confirmed yet, and from an expired link (no address known: the user types it). "Send the link again" answers
 * the same for every address; "Wrong address?" moves an unconfirmed account to another one, with its password.
 */
const CheckEmail: React.FC = () => {
  const state = useLocation().state as CheckEmailState;
  const [email, setEmail] = useState(state?.email || '');
  const knownEmail = !!state?.email;
  const [cooldown, setCooldown] = useState(state?.sent ? RESEND_COOLDOWN_SECONDS : 0);
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [changing, setChanging] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [password, setPassword] = useState('');

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = window.setTimeout(() => setCooldown((seconds) => seconds - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [cooldown]);

  const resend = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setNotice('');
    setSending(true);
    try {
      setNotice(await authService.resendConfirmation(email.trim(), getPendingInviteCode()));
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      setError(getErrorMessage(err, 'The link could not be sent. Please try again.'));
    } finally {
      setSending(false);
    }
  };

  const changeEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setNotice('');
    setSending(true);
    try {
      const changed = await authService.changeUnconfirmedEmail(email.trim(), password, newEmail.trim(), getPendingInviteCode());
      setEmail(changed);
      setChanging(false);
      setPassword('');
      setNewEmail('');
      setNotice(`We sent a new link to ${changed}.`);
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      setError(getErrorMessage(err, 'The address could not be changed. Please try again.'));
    } finally {
      setSending(false);
    }
  };

  return (
    <AuthShell>
      <div className="flex flex-col items-center text-center">
        <span className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-blue-50 text-blue-600 dark:bg-blue-950/60 dark:text-blue-300">
          <MailCheck className="w-6 h-6" aria-hidden="true" />
        </span>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Check your email</h2>
        {knownEmail ? (
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            {state?.fromLogin ? 'Your account is waiting for you to confirm ' : 'We sent a confirmation link to '}
            <strong className="font-semibold text-gray-900 dark:text-white break-all">{email}</strong>
            {state?.fromLogin
              ? '. Open the link in the email we sent you to start using Webots Academy.'
              : '. Open it to confirm your address and start using Webots Academy.'}
          </p>
        ) : (
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Enter the address you registered with, and we'll send you a new confirmation link.
          </p>
        )}
      </div>

      <div className="mt-5 space-y-3 empty:hidden">
        {error && (
          <div className="alert alert-error" role="alert">
            <AlertCircle />
            <span>{error}</span>
          </div>
        )}
        {notice && (
          <div className="alert alert-success" role="status">
            <CheckCircle2 />
            <span>{notice}</span>
          </div>
        )}
      </div>

      {!changing ? (
        <form onSubmit={resend} className="mt-5 space-y-4">
          {!knownEmail && (
            <div>
              <label htmlFor="check-email-address" className="label">Email</label>
              <input
                id="check-email-address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input"
                autoComplete="email"
                required
              />
            </div>
          )}
          {knownEmail && (
            <div className="alert alert-info">
              <Info />
              <span>The link works for 48 hours, and an account not confirmed within 7 days is deleted. Nothing there? Check the spam folder, or send it again.</span>
            </div>
          )}
          <button type="submit" disabled={sending || cooldown > 0} className="btn btn-secondary btn-lg w-full">
            {sending && <Spinner className="w-4 h-4" />}
            {cooldown > 0 ? `Send the link again in ${formatCountdown(cooldown)}` : knownEmail ? 'Send the link again' : 'Send the link'}
          </button>
          {knownEmail && (
            <p className="text-center text-sm text-gray-600 dark:text-gray-400">
              Wrong address?{' '}
              <button type="button" className="link min-h-[2.25rem]" onClick={() => { setChanging(true); setError(''); setNotice(''); }}>
                Change it
              </button>
            </p>
          )}
        </form>
      ) : (
        <form onSubmit={changeEmail} className="mt-5 space-y-4" aria-labelledby="change-email-heading">
          <h3 id="change-email-heading" className="text-sm font-semibold text-gray-900 dark:text-white">Change the address</h3>
          <div>
            <label htmlFor="change-email-new" className="label">New email</label>
            <input
              id="change-email-new"
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              className="input"
              autoComplete="email"
              autoFocus
              required
            />
          </div>
          <div>
            <label htmlFor="change-email-password" className="label">Password</label>
            <input
              id="change-email-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input"
              autoComplete="current-password"
              aria-describedby="change-email-password-hint"
              required
            />
            <p id="change-email-password-hint" className="hint">The password you registered with.</p>
          </div>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button type="button" className="btn btn-secondary" onClick={() => { setChanging(false); setError(''); }}>
              Cancel
            </button>
            <button type="submit" disabled={sending} className="btn btn-primary">
              {sending && <Spinner className="w-4 h-4 text-white" />}
              Change and send the link
            </button>
          </div>
        </form>
      )}

      <p className="mt-6 text-center text-sm text-gray-600 dark:text-gray-400">
        Already confirmed?{' '}
        <Link to="/login" state={email ? { email } : undefined} className="link">
          Log in
        </Link>
      </p>
    </AuthShell>
  );
};

export default CheckEmail;
